import React, { useState, useMemo, useEffect } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { TABLE_CONFIG } from '@/constants';
import TableFilter from './TableFilter';

interface DataTableProps {
  rows: any[];
  columns: GridColDef[];
  loading?: boolean;
  onRowClick?: (params: any) => void;
  navigationPath: string;
  initialPage?: number;
  onPageChange?: (page: number) => void;
}

const DataTable: React.FC<DataTableProps> = ({ 
  rows, 
  columns, 
  loading = false, 
  onRowClick,
  navigationPath,
  initialPage = 0,
  onPageChange
}) => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState<number>(TABLE_CONFIG.DEFAULT_PAGE_SIZE);

  // Update currentPage when initialPage changes (when returning from edit)
  useEffect(() => {
    setCurrentPage(initialPage);
  }, [initialPage]);

  const handleRowClick = (params: any) => {
    // Save current page state before navigation
    if (onPageChange) {
      onPageChange(currentPage);
    }
    
    if (onRowClick) {
      onRowClick(params);
    } else {
      navigate(`${navigationPath}/${params.row.id}/edit`);
    }
  };

  // Apply filters to rows
  const filteredRows = useMemo(() => {
    if (filters.length === 0) return rows;

    return rows.filter(row => {
      return filters.every(filter => {
        const value = row[filter.column];
        const filterValue = filter.value.toLowerCase();
        
        if (filter.operator === 'isEmpty') {
          return !value || value === '';
        }
        if (filter.operator === 'isNotEmpty') {
          return value && value !== '';
        }
        
        if (!value) return false;
        
        const stringValue = String(value).toLowerCase();
        
        switch (filter.operator) {
          case 'contains':
            return stringValue.includes(filterValue);
          case 'equals':
            return stringValue === filterValue;
          case 'startsWith':
            return stringValue.startsWith(filterValue);
          case 'endsWith':
            return stringValue.endsWith(filterValue);
          default:
            return true;
        }
      });
    });
  }, [rows, filters]);

  // Ensure rows is always an array
  const safeRows = Array.isArray(filteredRows) ? filteredRows : [];

  return (
    <Box sx={{ width: '100%' }}>
      <TableFilter 
        columns={columns} 
        onFiltersChange={setFilters} 
      />
      <Box sx={{ height: 600 }}>
        <DataGrid
          rows={safeRows}
          columns={columns}
          loading={loading}
          pageSizeOptions={TABLE_CONFIG.PAGE_SIZE_OPTIONS}
          paginationModel={{ page: currentPage, pageSize: pageSize }}
          onPaginationModelChange={(model) => {
            setCurrentPage(model.page);
            setPageSize(model.pageSize);
            if (onPageChange) {
              onPageChange(model.page);
            }
          }}
          onRowClick={handleRowClick}
          disableRowSelectionOnClick
          getRowId={(row) => row.id || Math.random().toString()}
          sx={{ 
            bgcolor: 'background.paper', 
            color: 'text.primary',
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid',
              borderColor: 'divider',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'background.default',
              borderBottom: '2px solid',
              borderColor: 'divider',
            },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: 'background.default',
              borderTop: '1px solid',
              borderColor: 'divider',
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default DataTable; 